import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Text } from "../../theme/primitives";
import { formatDuration } from "../../lib/time";
import { waveformToHeights } from "../../lib/waveform";
import type { AudioMeta } from "../../matrix/timeline";

interface AudioPlayerProps {
  audio: AudioMeta;
  mine: boolean;
}

const BARS = 40;

/** Custom voice-message player: play/pause, waveform + progress, duration. */
export function AudioPlayer({ audio, mine }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [error, setError] = useState(false);

  const heights = useMemo(() => waveformToHeights(audio.waveform, BARS), [audio.waveform]);
  // On the dark (outgoing) bubble everything must be light to stay visible.
  const barColor = mine ? "rgba(247, 251, 253, 0.4)" : "var(--color-text-muted)";
  const barPlayed = mine ? "var(--color-accent-contrast)" : "var(--color-accent)";
  const metaColor = mine ? "rgba(247, 251, 253, 0.72)" : "var(--color-text-muted)";

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      if (el.duration && Number.isFinite(el.duration)) setProgress(el.currentTime / el.duration);
    };
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    const onErr = () => setError(true);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onErr);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onErr);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => setError(true));
    }
  };

  if (!audio.url || error) {
    return <Text size="sm" style={{ color: metaColor }}>🎤 Voice message (unavailable)</Text>;
  }

  const playedBars = Math.floor(progress * BARS);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", minWidth: 200 }}>
      <audio ref={audioRef} src={audio.url} preload="none" />
      <Button
        variant="icon"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        style={{
          width: 34,
          height: 34,
          background: mine ? "var(--color-accent-contrast)" : "var(--color-accent)",
          color: mine ? "var(--color-accent)" : "#fff",
        }}
      >
        {playing ? "❚❚" : "▶"}
      </Button>
      <div style={{ display: "flex", alignItems: "center", gap: 2, height: 28, flex: 1 }}>
        {heights.map((h, i) => (
          <span
            key={i}
            style={{
              width: 3,
              borderRadius: 2,
              height: `${Math.max(10, h * 100)}%`,
              background: i <= playedBars ? barPlayed : barColor,
            }}
          />
        ))}
      </div>
      <Text size="sm" style={{ color: metaColor }}>
        {formatDuration(audio.durationMs)}
      </Text>
    </div>
  );
}
